'use client';

import { useCallback } from 'react';
import { useAnalyticsModuleTracking } from '../AnalyticsModuleTracking';
import { trackModuleViewed } from '../eventHandlerTracking';
import { getModuleCTAType } from '../utils';
import { useModuleViewedTracking } from './useModuleViewedTracking';

/**
 * Reports one module impression: the GTM `Module Viewed` push and the Segment
 * `module_viewed` event, both carrying the attributes of the nearest
 * `AnalyticsModuleTrackingProvider`.
 *
 * `TrackableModule` fires this from its viewport observer, which covers every
 * module whose wrapper it can measure. A module whose root is `position: fixed`
 * leaves that wrapper with no area, so it opts out and calls this itself at the
 * moment it appears. Both paths go through here so the payload cannot drift
 * between them, and so a `fixed` module lands in the same metric as the rest.
 *
 * @param element - The module's own root, read for `cta_type`. Pass the element a
 *   visitor actually sees, not the tracking wrapper.
 */
export const useModuleViewedReport = () => {
  const trackViewedInGtm = useModuleViewedTracking();
  const { attributes } = useAnalyticsModuleTracking();

  return useCallback(
    (element: HTMLElement | null) => {
      trackViewedInGtm();

      trackModuleViewed({
        module_title: attributes.moduleTitle,
        module_name: attributes.moduleName,
        module_index: attributes.moduleIndex,
        module_type: attributes.moduleType,
        cta_type: element ? getModuleCTAType(element) : 'No CTA',
      });
    },
    [trackViewedInGtm, attributes],
  );
};
